import React, {useEffect, useRef, useState} from 'react';
import '../../css/Login.css';
import axios from '../../api/axios';
import useAuth from '../../hooks/useAuth';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import {buildAuthSession} from '../../helpers/AuthSession';
import {defaultAppRoute} from '../../helpers/DefaultAppRoute';

const LOGIN_URL = '/api/login';

const Login = () => {
  const {setAuth, persist, setPersist} = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const userRef = useRef();
  const errRef = useRef();

  const [user, setUser] = useState('');
  const [pwd, setPwd] = useState('');
  const [errMsg, setErrMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    userRef.current?.focus();
  }, []);

  useEffect(() => {
    setErrMsg('');
  }, [user, pwd]);

  useEffect(() => {
    localStorage.setItem('persist', persist);
  }, [persist]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const params = new URLSearchParams({
        username: user,
        password: pwd,
        persist: String(persist)
      });

      const response = await axios.post(
        `${LOGIN_URL}?${params.toString()}`,
        JSON.stringify({username: user, password: pwd}),
        {
          headers: {'Content-type': 'application/json'},
          withCredentials: true
        }
      );

      const session = buildAuthSession(response.data);
      setAuth(session);
      setUser('');
      setPwd('');

      const destination = location.state?.from?.pathname;
      navigate(destination && destination !== '/login' ? destination : defaultAppRoute(session), {replace: true});
    } catch (err) {
      if (!err?.response) {
        setErrMsg('Brak odpowiedzi serwera');
      } else if (err.response?.status === 400) {
        setErrMsg('Brak nazwy użytkownika lub hasła');
      } else if (err.response?.status === 403) {
        setErrMsg('Brak dostępu');
      } else {
        setErrMsg('Nieudane logowanie');
      }

      requestAnimationFrame(() => errRef.current?.focus());
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="qstaff-login" style={{backgroundImage: 'url(/images/background2.jpg)'}}>
      <div className="qstaff-login-backdrop" />

      <nav className="qstaff-login-nav">
        <Link className="qstaff-login-brand" to="/" aria-label="Qstaff - strona główna">
          <span className="qstaff-login-brand-mark">Q</span>
          <span className="qstaff-login-brand-name">QSTAFF</span>
        </Link>
        <Link className="qstaff-login-back" to="/">
          <i className="fas fa-arrow-left" aria-hidden="true" />
          Strona główna
        </Link>
      </nav>

      <section className="qstaff-login-shell">
        <div className="qstaff-login-intro">
          <div className="qstaff-login-eyebrow">
            <span />
            PANEL QSTAFF
          </div>
          <h1>Witaj ponownie.</h1>
          <p>
            Zaloguj się, aby przejść do grafików, danych pracowników, RCP,
            szkoleń i bieżących informacji operacyjnych.
          </p>

          <div className="qstaff-login-benefits">
            <div>
              <span className="qstaff-login-benefit-icon"><i className="fas fa-calendar-check" /></span>
              <div>
                <strong>Aktualny grafik i obsada</strong>
                <small>Planowanie pracy, dostępność i zmiany w jednym miejscu.</small>
              </div>
            </div>
            <div>
              <span className="qstaff-login-benefit-icon"><i className="fas fa-users" /></span>
              <div>
                <strong>Kadry i komunikacja</strong>
                <small>Dane pracowników, wnioski, dokumenty i powiadomienia.</small>
              </div>
            </div>
            <div>
              <span className="qstaff-login-benefit-icon"><i className="fas fa-chart-line" /></span>
              <div>
                <strong>Dane do decyzji</strong>
                <small>RCP, koszty pracy i raporty dostępne z jednego systemu.</small>
              </div>
            </div>
          </div>
        </div>

        <div className="qstaff-login-card-wrap">
          <div className="qstaff-login-card">
            <div className="qstaff-login-card-heading">
              <span className="qstaff-login-card-icon"><i className="fas fa-user-lock" /></span>
              <div>
                <span>Bezpieczny dostęp</span>
                <h2>Zaloguj się</h2>
              </div>
            </div>

            <p
              ref={errRef}
              className={errMsg ? 'qstaff-login-error' : 'offscreen'}
              aria-live="assertive"
              tabIndex="-1"
            >
              <i className="fas fa-exclamation-circle" aria-hidden="true" />
              {errMsg}
            </p>

            <form onSubmit={handleSubmit} className="qstaff-login-form">
              <div className="qstaff-login-field">
                <label htmlFor="username">Login</label>
                <div className="qstaff-login-input-wrap">
                  <i className="fas fa-user" aria-hidden="true" />
                  <input
                    type="text"
                    id="username"
                    ref={userRef}
                    autoComplete="username"
                    onChange={(e) => setUser(e.target.value)}
                    value={user}
                    required
                    placeholder="Wpisz login"
                  />
                </div>
              </div>

              <div className="qstaff-login-field">
                <label htmlFor="password">Hasło</label>
                <div className="qstaff-login-input-wrap">
                  <i className="fas fa-lock" aria-hidden="true" />
                  <input
                    type="password"
                    id="password"
                    autoComplete="current-password"
                    onChange={(e) => setPwd(e.target.value)}
                    value={pwd}
                    required
                    placeholder="Wpisz hasło"
                  />
                </div>
              </div>

              <label className="qstaff-login-persist" htmlFor="persist">
                <input
                  type="checkbox"
                  id="persist"
                  checked={persist}
                  onChange={() => setPersist(prev => !prev)}
                />
                <span>
                  <strong>Ufaj temu urządzeniu</strong>
                  <small>Pozostań zalogowany na tym urządzeniu.</small>
                </span>
              </label>

              <button className="qstaff-login-submit" type="submit" disabled={submitting}>
                <span>{submitting ? 'Logowanie...' : 'Zaloguj się'}</span>
                <i className={submitting ? 'fas fa-circle-notch fa-spin' : 'fas fa-arrow-right'} aria-hidden="true" />
              </button>
            </form>

            <div className="qstaff-login-card-footer">
              <i className="fas fa-shield-alt" aria-hidden="true" />
              Dostęp do aplikacji jest chroniony i zależny od roli użytkownika.
            </div>
          </div>
        </div>
      </section>

      <footer className="qstaff-login-footer">
        <span>Qstaff</span>
        <i />
        <span>System zarządzania personelem</span>
      </footer>
    </main>
  );
};

export default Login;

